import React, { useEffect, useRef, useState } from 'react';
import {
  Bike,
  MapPin,
  MessageCircle,
  MoreVertical,
  Phone,
  Store,
  User,
  UtensilsCrossed,
} from 'lucide-react';
import { ContactActionModal, buildOrderContact } from '../ContactActionModal';
import { StatusBadge } from './StatusBadge';

function RouteFooterLabel({ label, time, colorVariant = 'green' }) {
  return (
    <p
      className={`otd-order-card__route-footer otd-order-card__route-footer--${colorVariant}`}
    >
      <span className='otd-order-card__route-footer-label'>{label}</span>
      <span className='otd-order-card__route-footer-sep'>:</span>
      <span className='otd-order-card__route-footer-time'>{time ?? '--'}</span>
    </p>
  );
}

function RoutePlanNode({ children, complete = false, muted = false }) {
  return (
    <div
      className={`otd-order-card__route-node ${muted ? 'otd-order-card__route-node--muted' : ''} ${
        complete ? 'otd-order-card__route-node--complete' : ''
      }`}
      aria-hidden
    >
      {children}
    </div>
  );
}

function RoutePlanSegment({ etaLabel, lineComplete = false }) {
  return (
    <div className='otd-order-card__route-plan-segment'>
      <div
        className={`otd-order-card__route-plan-line ${
          lineComplete ? 'otd-order-card__route-plan-line--complete' : ''
        }`}
      />
      <span className='otd-order-card__route-plan-eta'>{etaLabel ?? '--'}</span>
    </div>
  );
}

function OrderCardRoutePlan({
  routeMode,
  routeEta,
  routeLabel,
  routeTime,
  routeFirstLegComplete,
  routeSecondLegComplete,
}) {
  const footerColor = routeMode === 'no_driver' ? 'yellow' : 'green';

  if (routeMode === 'no_driver') {
    return (
      <div className='otd-order-card__route-plan'>
        <div className='otd-order-card__route-plan-row'>
          <RoutePlanNode muted>
            <Bike size={12} />
          </RoutePlanNode>
          <RoutePlanSegment etaLabel={routeEta?.driverToKitchenLabel} lineComplete={false} />
          <RoutePlanNode>
            <Store size={12} />
          </RoutePlanNode>
        </div>
        <RouteFooterLabel label={routeLabel} time={routeTime} colorVariant={footerColor} />
      </div>
    );
  }

  return (
    <div className='otd-order-card__route-plan'>
      <div className='otd-order-card__route-plan-row'>
        <RoutePlanNode complete={routeFirstLegComplete}>
          <Bike size={12} />
        </RoutePlanNode>
        <RoutePlanSegment
          etaLabel={routeEta?.driverToKitchenLabel}
          lineComplete={routeFirstLegComplete}
        />
        <RoutePlanNode complete={routeFirstLegComplete || routeSecondLegComplete}>
          <Store size={12} />
        </RoutePlanNode>
        <RoutePlanSegment
          etaLabel={routeEta?.kitchenToCustomerLabel}
          lineComplete={routeSecondLegComplete}
        />
        <RoutePlanNode complete={routeSecondLegComplete}>
          <MapPin size={12} />
        </RoutePlanNode>
      </div>
      <RouteFooterLabel label={routeLabel} time={routeTime} colorVariant={footerColor} />
    </div>
  );
}

function OrderCardRouteProgress({
  routeMode,
  routeLabel,
  routeTime,
  routeEta,
  routeFirstLegComplete,
  routeSecondLegComplete,
}) {
  return (
    <OrderCardRoutePlan
      routeMode={routeMode}
      routeEta={routeEta}
      routeLabel={routeLabel}
      routeTime={routeTime}
      routeFirstLegComplete={routeFirstLegComplete}
      routeSecondLegComplete={routeSecondLegComplete}
    />
  );
}

function OrderCardRowMenu({ who, disabled, onSelectAction }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (disabled) return null;

  const handleAction = (type) => (event) => {
    event.stopPropagation();
    setOpen(false);
    onSelectAction(who, type);
  };

  return (
    <div className='otd-order-card__row-menu' ref={rootRef}>
      <button
        type='button'
        className='otd-order-card__menu'
        aria-label={`${who} actions`}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <MoreVertical size={18} />
      </button>
      {open ? (
        <div className='otd-order-card__menu-popover' role='menu'>
          <button
            type='button'
            className='otd-order-card__menu-item'
            role='menuitem'
            onClick={handleAction('call')}
          >
            <Phone size={15} />
            Call
          </button>
          <button
            type='button'
            className='otd-order-card__menu-item'
            role='menuitem'
            onClick={handleAction('sms')}
          >
            <MessageCircle size={15} />
            SMS
          </button>
        </div>
      ) : null}
    </div>
  );
}

function OrderCardRow({
  imageSrc,
  FallbackIcon,
  title,
  subtitle,
  titleMuted = false,
  contactWho,
  menuDisabled,
  onSelectAction,
}) {
  return (
    <div className='otd-order-card__row'>
      <div className='otd-order-card__thumb'>
        {imageSrc ? (
          <img className='otd-order-card__thumb-img' src={imageSrc} alt='' />
        ) : (
          <FallbackIcon size={13} aria-hidden />
        )}
      </div>
      <div className='otd-order-card__row-text'>
        <p className={`otd-order-card__title ${titleMuted ? 'otd-order-card__title--muted' : ''}`}>
          {title}
        </p>
        {subtitle ? <p className='otd-order-card__subtitle'>{subtitle}</p> : null}
      </div>
      {menuDisabled ? (
        <span className='otd-order-card__menu-spacer' aria-hidden />
      ) : (
        <OrderCardRowMenu who={contactWho} disabled={menuDisabled} onSelectAction={onSelectAction} />
      )}
    </div>
  );
}

export function OrderCard({ order, selected, onSelect }) {
  const rawOrder = order.raw;
  const driver = rawOrder?.driver_information ?? {};

  const [contactModal, setContactModal] = useState({ open: false, type: 'call', contact: null });

  const openContactModal = (who, type) => {
    setContactModal({
      open: true,
      type,
      contact: buildOrderContact(rawOrder, who, driver),
    });
  };

  const closeContactModal = () => setContactModal((prev) => ({ ...prev, open: false }));

  const handleContactSubmit = async (payload) => {
    if (payload.type === 'call' && payload.contact?.phone) {
      const digits = String(payload.contact.phone).replace(/[^\d+]/g, '');
      if (digits) window.open(`tel:${digits}`, '_self');
    }
    await new Promise((resolve) => setTimeout(resolve, 400));
  };

  const rows = [
    {
      key: 'kitchen',
      contactWho: 'kitchen',
      menuDisabled: false,
      imageSrc: order.kitchenImage,
      FallbackIcon: Store,
      title: order.restaurantName,
      subtitle: order.kitchenAddress || 'Kitchen',
    },
    {
      key: 'customer',
      contactWho: 'customer',
      menuDisabled: false,
      imageSrc: order.customerImage,
      FallbackIcon: User,
      title: order.customerName,
      subtitle: 'Customer',
    },
    {
      key: 'driver',
      contactWho: 'driver',
      menuDisabled: !order.hasDriver,
      imageSrc: order.hasDriver ? order.driverImage : null,
      FallbackIcon: Bike,
      title: order.hasDriver ? order.driverName : 'Driver Not Assigned',
      subtitle: order.hasDriver && order.driverSubtitle ? order.driverSubtitle : 'Driver',
      titleMuted: !order.hasDriver,
    },
  ];

  return (
    <div className='otd-order-card-wrap'>
      <div
        role='button'
        tabIndex={0}
        className={`otd-order-card ${selected ? 'otd-order-card--selected' : ''}`}
        onClick={() => onSelect(order.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(order.id);
          }
        }}
      >
        <section className='otd-order-card__header' aria-label='Order summary'>
          <div className='otd-order-card__id-row'>
            <UtensilsCrossed size={14} className='otd-order-card__icon' />
            <span className='otd-order-card__id'>#{order.orderNo}</span>
          </div>
          <StatusBadge status={order.raw?.internal_order_status} />
        </section>

        <section
          className='otd-order-card__route-section'
          aria-label={order.isPickupOrder ? 'Pickup order' : 'Delivery route'}
        >
          {order.isPickupOrder ? (
            <p className='otd-order-card__pickup-label'>Pickup order</p>
          ) : (
            <OrderCardRouteProgress
              routeMode={order.routeMode}
              routeLabel={order.routeLabel}
              routeTime={order.routeTime}
              routeEta={order.routeEta}
              routeFirstLegComplete={order.routeFirstLegComplete}
              routeSecondLegComplete={order.routeSecondLegComplete}
            />
          )}
        </section>

        <section className='otd-order-card__body' aria-label='Order contacts'>
          <div className='otd-order-card__rows'>
            {rows.map((row) => (
              <OrderCardRow key={row.key} {...row} onSelectAction={openContactModal} />
            ))}
          </div>
        </section>
      </div>

      <ContactActionModal
        open={contactModal.open}
        type={contactModal.type}
        contact={contactModal.contact}
        onClose={closeContactModal}
        onSubmit={handleContactSubmit}
      />
    </div>
  );
}
